import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../components/Seo';
import PageHero from '../components/PageHero';
import Reveal from '../components/Reveal';
import Locations from '../components/Locations';
import { Field } from '../components/Field';
import { ArrowRight, Check, Phone, SOCIAL_ICONS } from '../components/Icons';
import { COPY, LOCATIONS, SOCIALS } from '../data/site';
import { PHOTO } from '../data/media';
import { sendContactMessage } from '../services/iziApi';
import './Form.css';
import './Contact.css';

const TOPICS = [
  { value: 'General enquiry', label: 'General enquiry' },
  { value: 'Private hire & events', label: 'Private hire & events' },
  { value: 'Large group booking', label: 'Large group booking' },
  { value: 'Feedback', label: 'Feedback' },
  { value: 'Press & partnerships', label: 'Press & partnerships' },
  { value: 'Careers', label: 'Careers' },
];

const EMPTY = { firstName: '', lastName: '', email: '', phone: '', topic: TOPICS[0].value, message: '' };

function validate(v) {
  const e = {};
  if (v.firstName.trim().length < 2) e.firstName = 'Please add your first name.';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(v.email.trim())) e.email = 'We need a valid email to reply.';
  if (v.message.trim().length < 5) e.message = 'Tell us a little more.';
  return e;
}

export default function Contact() {
  const [values, setValues] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [serverError, setServerError] = useState('');

  const set = (key) => (e) => {
    setValues((v) => ({ ...v, [key]: e.target.value }));
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  };

  async function onSubmit(e) {
    e.preventDefault();
    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) {
      document.querySelector('.field.has-error .field__control')?.focus();
      return;
    }
    setStatus('sending');
    setServerError('');
    try {
      await sendContactMessage({
        name: `${values.firstName} ${values.lastName}`.trim(),
        email: values.email,
        phone: values.phone,
        topic: values.topic,
        message: values.message,
      });
      setStatus('done');
    } catch (err) {
      setStatus('error');
      setServerError(err.message);
    }
  }

  return (
    <>
      <Seo
        title="Contact"
        description="Get in touch with The Local — Bugolobi 0776077093, Kyanja 0764404997. Private hire, large groups, feedback and press."
        path="/contact"
      />

      <PageHero
        eyebrow="Say hello"
        title="Get in touch."
        lead={COPY.contactLead}
        image={PHOTO.spritz}
      />

      {/* Quick contact bar -------------------------------------------------- */}
      <section className="section--tight quickbar">
        <div className="page quickbar__inner">
          {LOCATIONS.map((l) => (
            <a key={l.id} className="quickbar__item" href={`tel:${l.phoneIntl}`}>
              <Phone size={16} />
              <span>
                <small>{l.name}</small>
                <strong className="tabular">{l.phone}</strong>
              </span>
            </a>
          ))}
          <div className="quickbar__socials">
            {SOCIALS.map((s) => {
              const Icon = SOCIAL_ICONS[s.id];
              return (
                <a key={s.id} href={s.url} target="_blank" rel="noopener noreferrer" aria-label={`${s.label} ${s.handle}`}>
                  <Icon size={17} />
                  <span>{s.handle}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Form --------------------------------------------------------------- */}
      <section className="section formpanel">
        <div className="page formpanel__grid">
          <div className="formpanel__main">
            {status === 'done' ? (
              <Reveal className="formdone">
                <span className="formdone__icon">
                  <Check size={26} />
                </span>
                <h2 className="display brand d3">Message sent.</h2>
                <p className="body">
                  Thank you, {values.firstName}. Your note is with the team and we'll
                  reply to <strong>{values.email}</strong>. If it's time-sensitive,
                  calling the branch is always quicker.
                </p>
                <div className="formdone__actions">
                  <button type="button" className="btn btn--ghost" onClick={() => { setValues(EMPTY); setStatus('idle'); }}>
                    <span>Send another</span>
                  </button>
                  <Link className="btn btn--gold" to="/bookings">
                    <span>Book a table</span>
                    <ArrowRight />
                  </Link>
                </div>
              </Reveal>
            ) : (
              <form className="form" onSubmit={onSubmit} noValidate>
                <Reveal className="form__row form__row--2">
                  <Field
                    label="First name"
                    name="firstName"
                    autoComplete="given-name"
                    required
                    value={values.firstName}
                    onChange={set('firstName')}
                    error={errors.firstName}
                  />
                  <Field
                    label="Last name"
                    name="lastName"
                    autoComplete="family-name"
                    value={values.lastName}
                    onChange={set('lastName')}
                  />
                </Reveal>

                <Reveal className="form__row form__row--2" delay={0.06}>
                  <Field
                    label="Email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    placeholder="you@example.com"
                    value={values.email}
                    onChange={set('email')}
                    error={errors.email}
                  />
                  <Field
                    label="Telephone"
                    name="phone"
                    type="tel"
                    autoComplete="tel"
                    placeholder="07XX XXX XXX"
                    value={values.phone}
                    onChange={set('phone')}
                  />
                </Reveal>

                <Reveal delay={0.12}>
                  <Field
                    label="What's it about?"
                    name="topic"
                    as="select"
                    value={values.topic}
                    onChange={set('topic')}
                    options={TOPICS}
                  />
                </Reveal>

                <Reveal delay={0.18}>
                  <Field
                    label="Message or feedback"
                    name="message"
                    as="textarea"
                    required
                    placeholder="However big or small — tell us everything."
                    value={values.message}
                    onChange={set('message')}
                    error={errors.message}
                  />
                </Reveal>

                {serverError ? (
                  <p className="form__server" role="alert">
                    {serverError}
                  </p>
                ) : null}

                <Reveal className="form__foot" delay={0.24}>
                  <button type="submit" className="btn btn--gold" disabled={status === 'sending'}>
                    <span>{status === 'sending' ? 'Sending…' : 'Send message'}</span>
                    {status === 'sending' ? null : <ArrowRight />}
                  </button>
                  <p className="form__small">We read every one. Replies usually land within a day.</p>
                </Reveal>
              </form>
            )}
          </div>

          <aside className="formaside">
            <Reveal className="formaside__card" delay={0.1}>
              <h3 className="display brand d4">Private hire</h3>
              <p className="formaside__body">
                Birthdays, work dinners, a room to yourselves. Tell us the date and
                the headcount and we'll put together something that fits.
              </p>
            </Reveal>
            <Reveal className="formaside__card" delay={0.16}>
              <h3 className="display brand d4">Working with us</h3>
              <p className="formaside__body">
                Kitchen, floor and bar — we hire on attitude first. Send a short note
                and what you've done before.
              </p>
            </Reveal>
            <Reveal className="formaside__card formaside__card--dark" delay={0.22}>
              <h3 className="display brand d4">Prefer to just come in?</h3>
              <p className="formaside__body">No booking needed for two. For anything bigger, reserve ahead.</p>
              <Link className="btn btn--gold btn--sm" to="/bookings">
                <span>Book a table</span>
                <ArrowRight />
              </Link>
            </Reveal>
          </aside>
        </div>
      </section>

      <Locations heading={false} className="section--deep" />
    </>
  );
}
